// Missing Remarketing Coverage — Audiences section, deterministic check.
// Shows the per-campaign user-list + custom-audience counts, sorted with missing-remarketing
// campaigns first so the actionable rows are at the top.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface CampaignCoverageRow {
  campaignId?: string;
  campaignName?: string;
  biddingStrategy?: string;
  userListCount: number;
  customAudienceCount: number;
}

interface MissingRemarketingShape {
  totalSearchCampaigns: number;
  missingCount: number;
  coveredCount: number;
  missingPct: number;
  campaigns: CampaignCoverageRow[];
}

const STRATEGY_LABEL: Record<string, string> = {
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  MAXIMIZE_CONVERSIONS: 'Max Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Max Conv. Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Imp. Share',
  TARGET_SPEND: 'Target Spend',
  PERCENT_CPC: 'Percent CPC'
};

function strategyLabel(s: string | undefined): string {
  if (!s) return '—';
  return STRATEGY_LABEL[s] ?? s;
}

export const MissingRemarketingCoverageEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as MissingRemarketingShape;
  const coveragePct = Math.round((1 - e.missingPct) * 100);

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Remarketing coverage
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} Search campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{coveragePct}%</div>
            <div className='audience-coverage__lbl'>Campaigns with user-list</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.missingCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Campaigns missing remarketing</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.coveredCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Campaigns covered</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-campaign breakdown
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--remarketing-coverage'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Bidding strategy</th>
              <th className='evidence__th--right'>User-list audiences</th>
              <th className='evidence__th--right'>Custom audiences</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => {
              const missing = c.userListCount === 0;
              return (
                <tr
                  key={c.campaignId ?? c.campaignName}
                  className={classNames({ 'evidence__row--flagged': missing })}
                >
                  <td>{c.campaignName ?? '(unnamed)'}</td>
                  <td>{strategyLabel(c.biddingStrategy)}</td>
                  <td className='evidence__td--right'>
                    {missing ? <strong>0</strong> : c.userListCount.toLocaleString()}
                  </td>
                  <td className='evidence__td--right'>{c.customAudienceCount.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        First-party audiences = user lists (remarketing, customer match). Under Smart Bidding, these
        are read as <strong>signals</strong> in observation mode rather than bid multipliers — the
        bidder factors them into who sees ads without restricting reach. Non-brand campaigns benefit
        most; brand campaigns benefit less (the search term itself is a strong signal).
      </p>
    </div>
  );
};
